import { Component, useEffect, useMemo, useState, type ErrorInfo, type ReactNode } from 'react';
import { Link as LinkIcon, Copy, Check, RotateCcw, Ban, ExternalLink } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Sheet, SheetContent } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/Tooltip';
import { Skeleton } from '@/components/ui/Skeleton';
import { api, type BackendTask } from '@/lib/api';
import { TaskJsonView } from './TaskJsonView';
import {
  formatDurationHuman,
  formatDurationVsEta,
  durationMsToSeconds,
  relativeFromNow,
  progressRatio,
  shortId,
  computeDeltaSeconds,
  formatDeltaSeconds,
} from './format';
import { useRunningDuration } from '@/hooks/useRunningDuration';
import { SheetErrorBoundary } from '@/components/raios/SheetErrorBoundary';
import { STATUS_LABEL, type TaskStatus } from './types';

interface TaskDetailSheetProps {
  taskId: string | null;
  /**
   * Task vinda do board (TaskUI) com campos suficientes pra pintar o header
   * imediatamente, sem esperar GET /tasks/:id retornar. Reduz percepção de
   * latência e garante que id/handler/status SEMPRE aparecem na sheet.
   */
  initialTask?: import('./types').TaskUI | null;
  onClose: () => void;
  onTransition?: (taskId: string, nextStatus: TaskStatus) => void;
}

const STATUS_BADGE_CLASS: Record<TaskStatus, string> = {
  pending: 'bg-raios-warning/15 text-raios-warning-text border-raios-warning/30',
  running: 'bg-raios-accent/15 text-raios-accent-text border-raios-accent/30',
  done: 'bg-raios-success/15 text-raios-success-text border-raios-success/30',
  failed: 'bg-raios-danger/15 text-raios-danger-text border-raios-danger/30',
  retry: 'bg-raios-warning/15 text-raios-warning-text border-raios-warning/30',
  cancelled: 'bg-raios-elevated/30 text-raios-muted border-raios-elevated',
  paused: 'bg-raios-elevated/30 text-raios-muted border-raios-elevated',
};

export function TaskDetailSheet({ taskId, initialTask, onClose, onTransition }: TaskDetailSheetProps) {
  const [task, setTask] = useState<BackendTask | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('resumo');
  const [copied, setCopied] = useState(false);

  const open = taskId !== null;

  /**
   * Snapshot pra renderização imediata do header. Prioriza task (full do GET)
   * apenas se ela tem id string válido. Fallback hierárquico: task → initialTask
   * → taskId raw. Tipos de retorno garantem que campos críticos (id, handler,
   * status) NUNCA são undefined no consumo.
   */
  const headerSnapshot = useMemo<{
    id: string;
    title: string;
    handler: string;
    target_slug: string | null;
    status: TaskStatus;
    updated_at: string;
  } | null>(() => {
    const taskHasValidId = task && typeof task.id === 'string' && task.id.length > 0;
    if (task && taskHasValidId) {
      return {
        id: task.id,
        title:
          (typeof task.title === 'string' && task.title.trim()) ||
          (typeof task.target_slug === 'string' && task.target_slug) ||
          (typeof task.event_type === 'string' && task.event_type) ||
          '(sem título)',
        handler: typeof task.handler === 'string' ? task.handler : '',
        target_slug: typeof task.target_slug === 'string' ? task.target_slug : null,
        status: (task.status as TaskStatus) ?? 'pending',
        updated_at: typeof task.updated_at === 'string' ? task.updated_at : '',
      };
    }
    if (initialTask) {
      return {
        id: initialTask.id,
        title: initialTask.title,
        handler: initialTask.handler,
        target_slug: initialTask.target_slug,
        status: initialTask.status,
        updated_at: initialTask.updated_at,
      };
    }
    if (typeof taskId === 'string' && taskId.length > 0) {
      return {
        id: taskId,
        title: '',
        handler: '',
        target_slug: null,
        status: 'pending',
        updated_at: '',
      };
    }
    return null;
  }, [task, initialTask, taskId]);

  useEffect(() => {
    if (!taskId) {
      let cancelled = false;
      Promise.resolve().then(() => {
        if (!cancelled) setTask(null);
      });
      return () => {
        cancelled = true;
      };
    }
    let cancelled = false;
    Promise.resolve().then(() => {
      if (cancelled) return;
      setIsLoading(true);
      setError(null);
    });
    api
      .getTask(taskId)
      .then((t) => {
        if (cancelled) return;
        // Guarda shape mínimo: id deve ser string não-vazia. Sem isso o
        // header crasha em qualquer .slice(). Origem possível: payload {}
        // de RLS-blocked, retorno parcial em deserialização ou bug de cache.
        if (!t || typeof t !== 'object' || typeof (t as BackendTask).id !== 'string' || !(t as BackendTask).id) {
          console.warn('[TaskDetailSheet] getTask payload sem id válido', { taskId, raw: t });
          setError('Resposta do servidor sem ID — task pode estar inacessível.');
          return;
        }
        setTask(t);
      })
      .catch((err) => {
        console.error('[TaskDetailSheet] getTask falhou', taskId, err);
        if (!cancelled) setError(err instanceof Error ? err.message : 'Falha ao carregar task');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  /**
   * Handler global pra capturar erros que escapam do React Error Boundary
   * (event handlers, promises não-tratadas, async em useEffect). Loga no
   * console com prefixo identificável pra admin copiar e mandar pra debug.
   */
  useEffect(() => {
    if (!taskId) return;
    function handleError(e: ErrorEvent) {
      console.error('[TaskDetailSheet] window error · task', taskId, e.error ?? e.message, e);
    }
    function handleRejection(e: PromiseRejectionEvent) {
      console.error('[TaskDetailSheet] unhandled rejection · task', taskId, e.reason, e);
    }
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [taskId]);

  // Reset tab quando muda task
  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) setActiveTab('resumo');
    });
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  const tabs = useMemo(() => {
    if (!task) return [{ value: 'resumo', label: 'Resumo' }];
    const t = task;
    const items = [{ value: 'resumo', label: 'Resumo' }];
    if (t.objective || t.rationale || t.real_problem || t.risk_if_skipped || t.historical_context) {
      items.push({ value: 'briefing', label: 'Briefing' });
    }
    if (
      t.acceptance_criteria ||
      t.definition_of_done ||
      t.anti_definition_of_done ||
      t.preconditions ||
      t.postconditions ||
      t.gotchas ||
      t.validation_commands ||
      t.tools_allowed ||
      // P1.12 Frente L — rich fields novos do pipeline canonical v3.0.0
      t.expected_artifacts ||
      t.expected_input ||
      t.expected_output
    ) {
      items.push({ value: 'plano', label: 'Plano' });
    }
    items.push({ value: 'execucao', label: 'Execução' });
    items.push({ value: 'metricas', label: 'Métricas' });
    return items;
  }, [task]);

  const handleCopyId = () => {
    const id = task?.id ?? headerSnapshot?.id;
    if (!id) return;
    void navigator.clipboard.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="!w-full sm:!w-[640px] !max-w-[640px] overflow-hidden"
        aria-describedby="task-detail-desc"
      >
        {/* Acessibilidade: Radix Dialog exige Title + Description. */}
        <DialogPrimitive.Title className="sr-only">
          {headerSnapshot?.title || `Task ${shortId(headerSnapshot?.id, '')}`}
        </DialogPrimitive.Title>
        <DialogPrimitive.Description id="task-detail-desc" className="sr-only">
          Detalhes da task selecionada com tabs Resumo, Briefing, Plano, Execução e Métricas.
        </DialogPrimitive.Description>
        <SheetErrorBoundary resetKey={taskId} logLabel="TaskDetailSheet">
        <TooltipProvider delayDuration={200}>
          <div className="flex flex-col h-full">
            {/* Header — sempre renderiza usando headerSnapshot (taskId/initialTask),
                independente do GET /tasks/:id ter retornado. */}
            <header className="px-4 pt-4 pb-3 border-b border-raios-elevated/40 shrink-0">
              {error ? (
                <div className="text-raios-danger-text text-sm">{error}</div>
              ) : headerSnapshot ? (
                <>
                  <div className="flex items-center gap-2 text-[11px] text-raios-text-secondary mb-1.5">
                    <span className="font-medium">
                      {headerSnapshot.handler.replace(/^@/, '') || '—'}
                    </span>
                    {headerSnapshot.target_slug && (
                      <>
                        <span className="text-raios-muted">›</span>
                        <span className="text-raios-text">{headerSnapshot.target_slug}</span>
                      </>
                    )}
                  </div>
                  <h2 className="text-base font-semibold text-raios-text leading-snug pr-8 mb-2">
                    {headerSnapshot.title || '(sem título)'}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className={cn(
                        'uppercase text-[10px] tracking-wider border',
                        STATUS_BADGE_CLASS[headerSnapshot.status] ?? STATUS_BADGE_CLASS.pending,
                      )}
                    >
                      {STATUS_LABEL[headerSnapshot.status] ?? String(headerSnapshot.status)}
                    </Badge>
                    <span className="text-[11px] text-raios-muted font-mono inline-flex items-center gap-1">
                      <button
                        onClick={handleCopyId}
                        className="hover:text-raios-text inline-flex items-center gap-1"
                        aria-label="Copiar ID"
                        title={headerSnapshot.id}
                      >
                        {copied ? <Check className="h-3 w-3 text-raios-success-text" /> : <Copy className="h-3 w-3" />}
                        {shortId(headerSnapshot.id, '#')}
                      </button>
                    </span>
                    {headerSnapshot.updated_at && (
                      <span className="text-[11px] text-raios-muted">
                        atualizado {relativeFromNow(headerSnapshot.updated_at)}
                      </span>
                    )}
                    {isLoading && !task && (
                      <span className="text-[11px] text-raios-muted inline-flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-raios-accent animate-pulse" />
                        carregando detalhes
                      </span>
                    )}
                    <div className="ml-auto flex items-center gap-1">
                      {(headerSnapshot.status === 'failed' || headerSnapshot.status === 'cancelled') && onTransition && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onTransition(headerSnapshot.id, 'pending')}
                              className="h-7 w-7 p-0"
                              aria-label="Reexecutar"
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Reexecutar</TooltipContent>
                        </Tooltip>
                      )}
                      {(headerSnapshot.status === 'pending' || headerSnapshot.status === 'running' || headerSnapshot.status === 'retry') && onTransition && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onTransition(headerSnapshot.id, 'cancelled')}
                              className="h-7 w-7 p-0"
                              aria-label="Cancelar"
                            >
                              <Ban className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Cancelar</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </header>

            {/* Tabs — implementação inline (substitui DS Tabs pra reduzir surface de
                erro e remover dependência de useMediaQuery interno do componente). */}
            <div className="px-4 pt-2 border-b border-raios-elevated/40 shrink-0">
              <div role="tablist" className="flex gap-1">
                {tabs.map((t) => {
                  const active = t.value === activeTab;
                  return (
                    <button
                      key={t.value}
                      role="tab"
                      type="button"
                      aria-selected={active}
                      onClick={() => setActiveTab(t.value)}
                      className={cn(
                        'px-3 py-2 text-xs font-medium -mb-px border-b-2 rounded-t-md transition-colors',
                        active
                          ? 'border-raios-accent text-raios-accent-text'
                          : 'border-transparent text-raios-muted hover:text-raios-text-secondary',
                      )}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4">
              {isLoading && !task && <Skeleton className="h-40" />}
              {task && (
                <TabErrorBoundary key={`${task.id}-${activeTab}`} taskId={task.id} tab={activeTab}>
                  {activeTab === 'resumo' && <ResumoTab task={task} />}
                  {activeTab === 'briefing' && <BriefingTab task={task} />}
                  {activeTab === 'plano' && <PlanoTab task={task} />}
                  {activeTab === 'execucao' && <ExecucaoTab task={task} />}
                  {activeTab === 'metricas' && <MetricasTab task={task} />}
                </TabErrorBoundary>
              )}
            </div>
          </div>
        </TooltipProvider>
        </SheetErrorBoundary>
      </SheetContent>
    </Sheet>
  );
}

/* ─── Error Boundary das tabs (defesa contra crash em payload inesperado) ── */

interface TabErrorBoundaryProps {
  children: ReactNode;
  taskId: string;
  tab: string;
}

interface TabErrorBoundaryState {
  hasError: boolean;
  message: string;
}

class TabErrorBoundary extends Component<TabErrorBoundaryProps, TabErrorBoundaryState> {
  state: TabErrorBoundaryState = { hasError: false, message: '' };

  static getDerivedStateFromError(error: Error): TabErrorBoundaryState {
    return { hasError: true, message: error.message || 'Erro inesperado' };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[TaskDetailSheet] crash em tab', this.props.tab, 'task', this.props.taskId, error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-md border border-raios-danger/40 bg-raios-danger/5 p-4 text-sm">
          <p className="font-semibold text-raios-danger-text mb-1">Erro ao renderizar esta aba.</p>
          <p className="text-raios-text-secondary text-xs mb-2">{this.state.message}</p>
          <p className="text-raios-muted text-[11px]">
            Os outros campos da task continuam acessíveis nas demais tabs. O erro foi logado no console.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ─── Tab: Resumo ─────────────────────────────────────────────────────────── */

function ResumoTab({ task }: { task: BackendTask }) {
  // G4 F1 (D-G4-F): backend canonical = duration_ms (G3 PR-P1 dropou duration_seconds).
  // P1.15 Z2 (D-G4-G): useRunningDuration calcula runtime quando running + duration_ms NULL
  // (re-render a cada 1s); em terminal retorna duration_ms do backend direto.
  const effectiveDurationMs = useRunningDuration(task);
  const elapsed = durationMsToSeconds(effectiveDurationMs);
  const eta = task.estimated_duration_seconds ?? null;
  // P1.15 Z3: delta canonical frontend-derived a partir de duration_ms+ETA.
  // Só exibido em estado terminal (duration_ms populado pelo writer backend).
  const delta = computeDeltaSeconds(task.duration_ms ?? null, eta);

  return (
    <div className="flex flex-col gap-5">
      <Section title="Tempo">
        <KV label="Status" value={STATUS_LABEL[task.status as TaskStatus] ?? String(task.status ?? '—')} />
        <KV label="ETA" value={formatDurationHuman(eta)} mono />
        <KV
          label={task.status === 'done' ? 'Duração' : task.status === 'running' ? 'Decorrido' : 'Duração'}
          value={formatDurationVsEta(elapsed ?? null, task.status === 'running' ? eta : null)}
          mono
        />
        {delta !== null && (
          <KV
            label="Delta vs ETA"
            value={formatDeltaSeconds(delta)}
            mono
            valueClass={
              delta < 0
                ? 'text-raios-success-text'
                : delta > 0
                  ? 'text-raios-warning-text'
                  : ''
            }
          />
        )}
        <KV label="Criado" value={relativeFromNow(task.created_at)} />
        <KV label="Iniciado" value={relativeFromNow(task.started_at)} />
        <KV label="Concluído" value={relativeFromNow(task.completed_at)} />
      </Section>

      <Section title="Origem">
        {task.demand_id ? (
          <KVLink label="Demand" to={`/demands/${task.demand_id}`} value={shortId(task.demand_id, '#')} />
        ) : (
          <KV label="Demand" value="—" muted />
        )}
        {task.pipeline_run_id ? (
          <KVLink
            label="Pipeline run"
            to={`/pipelines/runs/${task.pipeline_run_id}`}
            value={shortId(task.pipeline_run_id, '#')}
            extra={task.pipeline_step_id ? `· step ${task.pipeline_step_id}` : undefined}
          />
        ) : (
          <KV label="Pipeline" value="—" muted />
        )}
        {task.session_id && <KV label="Session" value={shortId(task.session_id, '#')} mono />}
        {task.parent_task_id && <KV label="Task pai" value={shortId(task.parent_task_id, '#')} mono />}
        {task.created_by && <KV label="Created by" value={task.created_by} mono />}
        {task.closed_by && <KV label="Closed by" value={task.closed_by} mono />}
      </Section>

      <Section title="Execução">
        <KV label="Handler" value={task.handler} mono />
        <KV label="Target" value={task.target_slug ?? '—'} mono />
        {task.agent_id && <KV label="Agent" value={task.agent_id} mono />}
        <KV label="Retries" value={`${task.retry_count} / ${task.max_retries}`} />
        {task.urgency_level && <KV label="Urgência" value={task.urgency_level} />}
        {task.estimated_size && <KV label="Tamanho" value={task.estimated_size} />}
        {task.domain && <KV label="Domain" value={task.domain} />}
        {task.supervision_mode && <KV label="Supervisão" value={task.supervision_mode} />}
      </Section>

      {task.trace_id && (
        <Section title="Rastreio">
          <KV label="Trace" value={shortId(task.trace_id, '')} mono />
          {task.root_trace_id && <KV label="Root trace" value={shortId(task.root_trace_id, '')} mono />}
          {task.idempotency_key && <KV label="Idempotency" value={task.idempotency_key} mono />}
          {task.event_type && <KV label="Event type" value={task.event_type} mono />}
        </Section>
      )}
    </div>
  );
}

/* ─── Tab: Briefing ───────────────────────────────────────────────────────── */

function BriefingTab({ task }: { task: BackendTask }) {
  return (
    <div className="flex flex-col gap-5">
      {task.objective && <Block title="Objetivo">{task.objective}</Block>}
      {task.rationale && <Block title="Racional">{task.rationale}</Block>}
      {task.real_problem && <Block title="Problema real">{task.real_problem}</Block>}
      {task.risk_if_skipped && <Block title="Risco se pulada">{task.risk_if_skipped}</Block>}
      {task.historical_context && <Block title="Contexto histórico">{task.historical_context}</Block>}
      {task.original_literal_context && (
        <Block title="Contexto original (citação)">{task.original_literal_context}</Block>
      )}
    </div>
  );
}

/* ─── Tab: Plano ──────────────────────────────────────────────────────────── */

function PlanoTab({ task }: { task: BackendTask }) {
  return (
    <div className="flex flex-col gap-5">
      <ListBlock title="Acceptance criteria" value={task.acceptance_criteria} numbered />
      <ListBlock title="Definition of Done" value={task.definition_of_done} />
      <ListBlock title="Anti Definition of Done" value={task.anti_definition_of_done} variant="danger" />
      {/* P1.12 Frente L — rich fields novos do pipeline canonical v3.0.0:
          expected_input/output são scalars descrevendo o contrato I/O da task;
          expected_artifacts é array de paths/refs que a task DEVE produzir. */}
      {task.expected_input && typeof task.expected_input === 'string' && (
        <Block title="Expected input">{task.expected_input}</Block>
      )}
      {task.expected_output && typeof task.expected_output === 'string' && (
        <Block title="Expected output">{task.expected_output}</Block>
      )}
      <ListBlock title="Expected artifacts" value={task.expected_artifacts} mono />
      <ListBlock title="Preconditions" value={task.preconditions} />
      <ListBlock title="Postconditions" value={task.postconditions} />
      <ListBlock title="Gotchas" value={task.gotchas} variant="warning" />
      <ListBlock title="Validation commands" value={task.validation_commands} mono />
      <ListBlock title="Tools allowed" value={task.tools_allowed} mono />
      <ListBlock title="Dependências externas" value={task.dependencies_external} />
      <ListBlock title="Affected parties" value={task.affected_parties} />
    </div>
  );
}

/* ─── Tab: Execução ───────────────────────────────────────────────────────── */

function ExecucaoTab({ task }: { task: BackendTask }) {
  // Backend ocasionalmente retorna evidence_refs com elementos não-string
  // (objetos vazios, números). Renderizar `{ref}` com objeto cru → React #31
  // (Objects are not valid as a React child). Coerção defensiva pra string aqui.
  const evidenceItems: string[] = Array.isArray(task.evidence_refs)
    ? task.evidence_refs
        .map((ref) => {
          if (typeof ref === 'string') return ref;
          if (ref === null || ref === undefined) return '';
          try {
            return JSON.stringify(ref);
          } catch {
            return String(ref);
          }
        })
        .filter((s) => s.length > 0)
    : [];

  return (
    <div className="flex flex-col gap-5">
      <div>
        <SectionHeader title="Input" />
        <TaskJsonView value={task.input ?? null} />
      </div>
      <div>
        <SectionHeader title="Output" />
        <TaskJsonView value={task.output ?? null} />
      </div>
      {task.error && (
        <div>
          <SectionHeader title="Error" tone="danger" />
          <TaskJsonView value={task.error} />
        </div>
      )}
      {task.execution_notes && typeof task.execution_notes === 'string' && (
        <Block title="Notas de execução">{task.execution_notes}</Block>
      )}
      {evidenceItems.length > 0 && (
        <div>
          <SectionHeader title="Evidence refs" />
          <ul className="flex flex-col gap-1.5">
            {evidenceItems.map((ref, i) => (
              <li key={i}>
                {looksLikeUrl(ref) ? (
                  <a
                    href={ref}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 text-raios-accent-text hover:underline text-xs break-all"
                  >
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    {ref}
                  </a>
                ) : (
                  <span className="text-xs text-raios-text-secondary font-mono break-all">{ref}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {task.outcome && typeof task.outcome === 'string' && <Block title="Outcome">{task.outcome}</Block>}
    </div>
  );
}

/* ─── Tab: Métricas ───────────────────────────────────────────────────────── */

function MetricasTab({ task }: { task: BackendTask }) {
  // G4 F1 (D-G4-F): backend canonical = duration_ms (G3 PR-P1 dropou duration_seconds).
  // P1.15 Z2 (D-G4-G): useRunningDuration calcula runtime quando running + duration_ms NULL
  // (re-render a cada 1s); em terminal retorna duration_ms do backend direto.
  const effectiveDurationMs = useRunningDuration(task);
  const elapsed = durationMsToSeconds(effectiveDurationMs);
  const eta = task.estimated_duration_seconds ?? null;
  const ratio = progressRatio(elapsed ?? null, eta);
  // P1.15 Z3: delta canonical frontend-derived (consistente com TaskCard +
  // ResumoTab). Backend não expõe coluna canônica delta — calculamos local.
  const delta = computeDeltaSeconds(task.duration_ms ?? null, eta);

  const hasAnything =
    elapsed !== null || eta !== null || delta !== null || (task.retry_count ?? 0) > 0;

  if (!hasAnything) {
    return (
      <div className="text-sm text-raios-muted">
        Esta task não tem ETA, duração ou retries registrados — sem métricas pra mostrar.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Duração: comparação visual elapsed vs ETA */}
      <section>
        <SectionHeader title="Tempo de execução" />
        <p className="text-[11px] text-raios-muted mb-3">
          Quanto a task levou comparado com a estimativa pré-definida (ETA).
        </p>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <Stat label="Decorrido" value={formatDurationHuman(elapsed)} />
          <Stat label="ETA" value={formatDurationHuman(eta)} />
          <Stat
            label="Delta"
            value={formatDeltaSeconds(delta)}
            tone={delta === null ? undefined : delta < 0 ? 'good' : delta > 0 ? 'warn' : undefined}
          />
        </div>
        {ratio !== null && (
          <div>
            <div className="h-2 w-full bg-raios-elevated/40 rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full transition-[width]',
                  ratio < 0.7 ? 'bg-raios-success' : ratio < 1 ? 'bg-raios-warning' : 'bg-raios-danger',
                )}
                style={{ width: `${Math.min(100, Math.round(ratio * 100))}%` }}
              />
            </div>
            <p className="text-[11px] text-raios-muted mt-1 tabular-nums">
              {Math.round(ratio * 100)}% do ETA consumido
            </p>
          </div>
        )}
        {delta !== null && (
          <p className="text-xs text-raios-text-secondary mt-3">
            {delta < 0
              ? `Foi ${formatDurationHuman(Math.abs(delta))} mais rápido que o estimado.`
              : delta > 0
                ? `Demorou ${formatDurationHuman(delta)} a mais que o estimado.`
                : 'Bateu o estimado exato.'}
          </p>
        )}
      </section>

      {/* Retries */}
      <section>
        <SectionHeader title="Retries" />
        <p className="text-[11px] text-raios-muted mb-3">
          Quantas vezes a task foi reexecutada após falha. Limite definido em <code>max_retries</code>.
        </p>
        <div className="flex items-center gap-3">
          <span className="text-2xl font-semibold tabular-nums text-raios-text">
            {task.retry_count ?? 0}
          </span>
          <span className="text-sm text-raios-muted">de {task.max_retries ?? 0}</span>
          {(task.retry_count ?? 0) > 0 && (
            <Badge variant="outline" className="border-raios-warning/40 text-raios-warning-text">
              já reexecutada
            </Badge>
          )}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'warn' }) {
  return (
    <div className="rounded-md border border-raios-elevated/40 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wider text-raios-muted">{label}</p>
      <p
        className={cn(
          'text-base font-semibold tabular-nums',
          tone === 'good' && 'text-raios-success-text',
          tone === 'warn' && 'text-raios-warning-text',
          !tone && 'text-raios-text',
        )}
      >
        {value}
      </p>
    </div>
  );
}

/* ─── Helpers visuais ─────────────────────────────────────────────────────── */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <SectionHeader title={title} />
      <div className="grid grid-cols-[100px_1fr] gap-y-1.5 gap-x-3">{children}</div>
    </section>
  );
}

function SectionHeader({ title, tone }: { title: string; tone?: 'danger' }) {
  return (
    <h3
      className={cn(
        'text-[10px] uppercase tracking-wider font-semibold mb-2',
        tone === 'danger' ? 'text-raios-danger-text' : 'text-raios-muted',
      )}
    >
      {title}
    </h3>
  );
}

function KV({
  label,
  value,
  mono = false,
  muted = false,
  valueClass,
}: {
  label: string;
  value: string;
  mono?: boolean;
  muted?: boolean;
  valueClass?: string;
}) {
  return (
    <>
      <span className="text-[11px] text-raios-muted">{label}</span>
      <span
        className={cn(
          'text-xs',
          mono && 'font-mono',
          muted && 'text-raios-muted',
          !muted && 'text-raios-text',
          valueClass,
        )}
      >
        {value}
      </span>
    </>
  );
}

function KVLink({
  label,
  to,
  value,
  extra,
}: {
  label: string;
  to: string;
  value: string;
  extra?: string;
}) {
  return (
    <>
      <span className="text-[11px] text-raios-muted">{label}</span>
      <span className="text-xs">
        <RouterLink to={to} className="inline-flex items-center gap-1 text-raios-accent-text hover:underline">
          <LinkIcon className="h-3 w-3" />
          <span className="font-mono">{value}</span>
        </RouterLink>
        {extra && <span className="text-raios-muted ml-1.5">{extra}</span>}
      </span>
    </>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <SectionHeader title={title} />
      <p className="text-sm text-raios-text whitespace-pre-wrap leading-relaxed">{children}</p>
    </section>
  );
}

interface ListBlockProps {
  title: string;
  value: string[] | string | null | undefined;
  numbered?: boolean;
  mono?: boolean;
  variant?: 'default' | 'warning' | 'danger';
}

function ListBlock({ title, value, numbered, mono, variant = 'default' }: ListBlockProps) {
  const items = useMemo(() => normalizeList(value), [value]);
  if (items.length === 0) return null;

  const variantClass = {
    default: 'text-raios-text',
    warning: 'text-raios-warning-text',
    danger: 'text-raios-danger-text',
  }[variant];

  return (
    <section>
      <SectionHeader title={title} tone={variant === 'danger' ? 'danger' : undefined} />
      <ul className={cn('flex flex-col gap-1', mono && 'font-mono')}>
        {items.map((item, i) => (
          <li key={i} className={cn('text-xs flex gap-2', variantClass)}>
            <span className="text-raios-muted shrink-0 tabular-nums">{numbered ? `${i + 1}.` : '•'}</span>
            <span className="break-words">{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function normalizeList(v: unknown): string[] {
  if (!v) return [];
  if (Array.isArray(v)) {
    return v.flatMap((x) => (typeof x === 'string' ? [x] : x ? [JSON.stringify(x)] : []));
  }
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.map((x) => (typeof x === 'string' ? x : JSON.stringify(x)));
      } catch { /* fallthrough */ }
    }
    return [trimmed];
  }
  return [];
}

function looksLikeUrl(s: string): boolean {
  return /^https?:\/\//.test(s);
}
